'use client'

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight, Check, Search } from 'lucide-react'

interface DropdownOption {
  id: string | number | null
  name: string
  isSelected?: boolean
  fontFamily?: string // Optional: font to display option in
  isHeader?: boolean // Category header
  category?: string // Category name for grouping
}

interface CustomDropdownProps {
  label: string
  currentValue: string
  options: DropdownOption[]
  onSelect: (id: string | number | null) => void
  placeholder?: string
  onSearch?: (query: string) => void
  isSearching?: boolean
}

export function CustomDropdown({
  label,
  currentValue,
  options,
  onSelect,
  placeholder = 'Select...',
  onSearch,
  isSearching = false
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const dropdownContentRef = useRef<HTMLDivElement>(null)
  // Calculate dropdown position
  const updatePosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      })
    }
  }

  // Load fonts for preview when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const fontSet = new Set<string>()
      options.forEach(opt => {
        if (opt.fontFamily) {
          fontSet.add(opt.fontFamily)
        }
      })
      
      // Manually load fonts instead of using the hook in a loop
      const fontsArray = Array.from(fontSet)
      fontsArray.forEach(fontFamily => {
        if (!fontFamily || fontFamily === 'Inter') return
        
        // Dynamic import of font URLs
        const fontUrls: Record<string, string> = {
          'Roboto': 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
          'Open Sans': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700&display=swap',
          'Lato': 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap',
          'Poppins': 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
          'Montserrat': 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap',
          'Nunito': 'https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700&display=swap',
          'Work Sans': 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap',
          'Plus Jakarta Sans': 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap',
          'DM Sans': 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;700&display=swap',
          'Merriweather': 'https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700;900&display=swap',
          'Playfair Display': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;900&display=swap',
          'Lora': 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap',
          'Source Serif Pro': 'https://fonts.googleapis.com/css2?family=Source+Serif+Pro:wght@300;400;600;700&display=swap',
          'Crimson Text': 'https://fonts.googleapis.com/css2?family=Crimson+Text:wght@400;600;700&display=swap',
          'PT Serif': 'https://fonts.googleapis.com/css2?family=PT+Serif:wght@400;700&display=swap',
          'Bebas Neue': 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap',
          'Righteous': 'https://fonts.googleapis.com/css2?family=Righteous&display=swap',
          'Oswald': 'https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;700&display=swap',
          'Anton': 'https://fonts.googleapis.com/css2?family=Anton&display=swap',
          'Archivo Black': 'https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap',
          'JetBrains Mono': 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;700&display=swap',
          'Fira Code': 'https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;700&display=swap',
          'Source Code Pro': 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@300;400;500;700&display=swap',
          'IBM Plex Mono': 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;700&display=swap',
          'Space Mono': 'https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap',
          'Pacifico': 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap',
          'Caveat': 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap',
          'Dancing Script': 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap',
          'Satisfy': 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap',
          'Inter Display': 'https://fonts.googleapis.com/css2?family=Inter+Display:wght@300;400;500;600;700&display=swap',
          'Cairo': 'https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900&display=swap',
          'Tajawal': 'https://fonts.googleapis.com/css2?family=Tajawal:wght@200;300;400;500;700;800;900&display=swap',
          'Noto Naskh Arabic': 'https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;500;600;700&display=swap',
          'Amiri': 'https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap',
          'Almarai': 'https://fonts.googleapis.com/css2?family=Almarai:wght@300;400;700;800&display=swap',
          'IBM Plex Sans Arabic': 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@100;200;300;400;500;600;700&display=swap',
          'Lateef': 'https://fonts.googleapis.com/css2?family=Lateef:wght@200;300;400;500;600;700;800&display=swap',
          'Rakkas': 'https://fonts.googleapis.com/css2?family=Rakkas&display=swap',
        }
        
        const fontUrl = fontUrls[fontFamily]
        if (!fontUrl) return
        
        // Check if link already exists
        const existingLink = document.querySelector(`link[href="${fontUrl}"]`)
        if (existingLink) return
        
        // Create and append link element
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = fontUrl
        document.head.appendChild(link)
      })
    }
  }, [isOpen, options])

  // Filter options based on search term (client-side fallback)
  const filteredOptions = onSearch
    ? options  // Server-side search, show all results
    : options.filter(option =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase())
      )

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    if (onSearch) {
      onSearch(value)  // Server-side search
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      // Check if click is outside both the trigger button AND the dropdown content
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        dropdownContentRef.current &&
        !dropdownContentRef.current.contains(target)
      ) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    const handleScroll = () => {
      if (isOpen) {
        updatePosition()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
      // Listen for scroll on all parent elements and window
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      
      // Initial position calculation
      updatePosition()
      
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleScroll)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm border border-input bg-background rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className="text-muted-foreground truncate font-medium">{label}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-foreground truncate max-w-[200px]">{currentValue || placeholder}</span>
          <ChevronRight
            className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`}
          />
        </div>
      </button>

      {isOpen && typeof window !== 'undefined' && createPortal(
        <div 
          ref={dropdownContentRef}
          className="fixed bg-background border border-border rounded-lg shadow-2xl min-w-[300px] max-w-md"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 99999,
            pointerEvents: 'auto'
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="p-3 border-b border-border" style={{ pointerEvents: 'auto' }}>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={onSearch ? "Type to search..." : "Search..."}
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                style={{ pointerEvents: 'auto' }}
              />
              {isSearching && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              )}
            </div>
          </div>

          {/* Options List */}
          <div
            ref={scrollContainerRef}
            className="max-h-96 overflow-y-auto overscroll-contain scroll-smooth"
            style={{ pointerEvents: 'auto' }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                // Render category header
                if (option.isHeader) {
                  return (
                    <div 
                      key={`header-${option.category || index}`}
                      className="px-4 py-2 text-xs font-semibold text-muted-foreground bg-muted/30"
                    >
                      {option.name}
                    </div>
                  )
                }
                
                // Render regular option
                return (
                  <button
                    key={option.id || 'none'}
                    type="button"
                    onClick={() => {
                      onSelect(option.id)
                      setIsOpen(false)
                      setSearchTerm('')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-muted/50 text-foreground"
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                  >
                    <div className="w-4 h-4 flex-shrink-0">
                      {option.isSelected && <Check className="w-4 h-4 text-foreground" />}
                    </div>
                    <div className="truncate flex-1">
                      <span
                        className="font-preview-option"
                        style={option.fontFamily ? {
                          fontFamily: `${option.fontFamily}, sans-serif`,
                          ['--preview-font' as any]: option.fontFamily
                        } : {}}
                      >
                        {option.name}
                      </span>
                    </div>
                  </button>
                )
              })
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {searchTerm ? 'No results found' : 'Start typing to search...'}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
